/**
 * Order entry → trading-socket commands (SPEC §9.1–§9.3, §9.7, §13.1; ADR-0021).
 *
 * Pure: what a click means (side, type, price in integer ticks) and the exact protobuf messages
 * the trading socket carries (`PlaceOrder`, `ModifyOrder`, `CancelOrder`, `ExitAction`). Nothing
 * here sends; `TradingCommands` does, one request per user action and never a retry (§1.3).
 * Messages are built as plain protobuf-es v2 objects (`$typeName` + every field), so this module
 * only type-imports generated code and runs under `node --test`.
 */
import type {
  CancelOrder,
  ExitAction,
  ForceExit,
  ModifyOrder,
  OrderEvent,
  PlaceOrder,
  Resync,
} from "../gen/helm/v1/trading_pb";
import { bracketSpecMessage, bracketWords, type OrderBracket } from "../brackets/order.ts";
import { ORDER_STATE, isWorkingOrder } from "./state.ts";

/** A proto enum value as a plain number (the mirrors below are plain numbers). */
const num = (v: number): number => v;

/** Mirrors proto `Side`. */
export const PB_SIDE = { BUY: 1, SELL: 2 } as const;
/** Mirrors proto `OrderType`. */
export const PB_ORDER_TYPE = { MARKET: 1, LIMIT: 2, STOP_MARKET: 3, STOP_LIMIT: 4 } as const;
/** Mirrors proto `Tif`. */
export const PB_TIF = { DAY: 1, GTC: 2 } as const;
/** Mirrors proto `Origin` (the manual ones a browser may send; ADR-0011 item 7). */
export const PB_ORIGIN = { DOM: 1, CHART: 2, TICKET: 3, SCALP_PAD: 4, HOTKEY: 5 } as const;
/** Mirrors proto `ORIGIN_BRACKET`: a bracket child the gateway placed (AUTO; never sent by a browser). */
export const PB_ORIGIN_BRACKET = 10;
/** Mirrors proto `BracketRole`. */
export const PB_BRACKET_ROLE = { ENTRY: 1, TARGET: 2, STOP: 3 } as const;
/** Mirrors proto `ExitKind`. */
export const PB_EXIT_KIND = {
  EXIT_AND_CANCEL_SYMBOL: 1,
  REVERSE_AND_CANCEL_SYMBOL: 2,
  CANCEL_SYMBOL_ORDERS: 3,
  CANCEL_BIDS: 4,
  CANCEL_OFFERS: 5,
  CANCEL_ALL_ACCOUNT: 6,
  FLATTEN_ACCOUNT: 7,
  FLATTEN_ALL_ACCOUNTS: 8,
} as const;

export type TradeSide = "buy" | "sell";
export type EntryType = "market" | "limit" | "stop" | "stopLimit";
export type EntryOrigin = keyof typeof PB_ORIGIN;
export type EntryTif = keyof typeof PB_TIF;

/** A new order as the user asked for it. Prices are integer ticks (SPEC §1.3). */
export type OrderIntent = {
  accountId: string;
  /** Resolved contract, e.g. "NQZ6" (the gateway refuses roots). */
  contract: string;
  side: TradeSide;
  type: EntryType;
  qty: number;
  limitTicks?: number;
  stopTicks?: number;
  tif?: EntryTif;
  origin: EntryOrigin;
  /** Stop / target legs the gateway places as the entry fills (ADR-0025). */
  bracket?: OrderBracket;
  /**
   * A take-profit (limit) or stop-loss (stop) for the whole open position, dragged off the
   * chart's position line (SPEC §9.3): the gateway attaches it to the position's bracket (one OCO
   * pair that follows the position's size). Never with `bracket`.
   */
  protectPosition?: boolean;
};

/** A fresh idempotency key per user action (PlaceOrder.client_order_id, SPEC §9.9 step 6). */
export function newRequestId(): string {
  return globalThis.crypto.randomUUID();
}

const PB_TYPE_OF: Readonly<Record<EntryType, number>> = {
  market: PB_ORDER_TYPE.MARKET,
  limit: PB_ORDER_TYPE.LIMIT,
  stop: PB_ORDER_TYPE.STOP_MARKET,
  stopLimit: PB_ORDER_TYPE.STOP_LIMIT,
};

/** Is the intent well formed (prices present and positive integer ticks, quantity ≥ 1)? */
export function checkIntent(i: OrderIntent): string | null {
  if (!Number.isInteger(i.qty) || i.qty < 1) return "Quantity must be a whole number of at least 1";
  const tick = (v: number | undefined): boolean => v !== undefined && Number.isInteger(v) && v > 0;
  if ((i.type === "limit" || i.type === "stopLimit") && !tick(i.limitTicks)) return "Limit price is required";
  if ((i.type === "stop" || i.type === "stopLimit") && !tick(i.stopTicks)) return "Stop price is required";
  return null;
}

/** `PlaceOrder` for an intent. Market orders carry no price at all (SPEC §6.9). */
export function placeOrderMessage(
  i: OrderIntent,
  clientOrderId: string,
  confirmedByUser = false,
): PlaceOrder {
  const limit = i.type === "limit" || i.type === "stopLimit" ? BigInt(i.limitTicks ?? 0) : 0n;
  const stop = i.type === "stop" || i.type === "stopLimit" ? BigInt(i.stopTicks ?? 0) : 0n;
  return {
    $typeName: "helm.v1.PlaceOrder",
    clientOrderId,
    accountId: i.accountId,
    symbol: i.contract,
    side: i.side === "buy" ? PB_SIDE.BUY : PB_SIDE.SELL,
    type: PB_TYPE_OF[i.type],
    qty: i.qty,
    limitTicks: limit,
    stopTicks: stop,
    trailTicks: 0,
    tif: PB_TIF[i.tif ?? "DAY"],
    origin: PB_ORIGIN[i.origin],
    confirmedByUser,
    strategyInstanceId: "",
    protectPosition: i.protectPosition === true,
    ...(i.bracket && i.protectPosition !== true ? { bracket: bracketSpecMessage(i.bracket) } : {}),
  };
}

export function cancelMessage(orderId: string): CancelOrder {
  return { $typeName: "helm.v1.CancelOrder", clientOrderId: orderId };
}

/** `ModifyOrder`: 0 means unchanged for every field (trading.proto). */
export function modifyMessage(
  orderId: string,
  change: { qty?: number; limitTicks?: number; stopTicks?: number },
): ModifyOrder {
  return {
    $typeName: "helm.v1.ModifyOrder",
    clientOrderId: orderId,
    qty: change.qty ?? 0,
    limitTicks: BigInt(change.limitTicks ?? 0),
    stopTicks: BigInt(change.stopTicks ?? 0),
    confirmedByUser: false,
  };
}

export type ExitActionKind = keyof typeof PB_EXIT_KIND;

/**
 * `ExitAction`. `confirmedByUser` is only ever true after the user confirmed a dialog;
 * `clientRequestId` is a fresh id per click (the gateway refuses a repeated Reverse & Cxl).
 */
export function exitMessage(
  kind: ExitActionKind,
  accountId: string,
  symbol: string,
  confirmedByUser = false,
  clientRequestId = "",
): ExitAction {
  return {
    $typeName: "helm.v1.ExitAction",
    accountId,
    symbol,
    kind: PB_EXIT_KIND[kind],
    confirmedByUser,
    clientRequestId,
  };
}

/**
 * The owner's "Force exit anyway" for an exit the gateway holds back (ADR-0050): always
 * `confirmed_by_user` — the UI sends it only from its confirmation dialog. `symbol` "" forces
 * every held exit of the account; `loginId` routes a copy follower on another login.
 */
export function forceExitMessage(accountId: string, symbol: string, loginId: string): ForceExit {
  return { $typeName: "helm.v1.ForceExit", accountId, symbol, confirmedByUser: true, loginId };
}

/**
 * "Resync with Rithmic" (ADR-0059), always confirmed by the user first: the gateway re-reads the
 * login's working orders, order history and positions from Rithmic and drops what Rithmic no longer
 * lists. Nothing is cancelled or sent. `loginId` empty = the session's own login.
 */
export function resyncMessage(loginId: string): Resync {
  return { $typeName: "helm.v1.Resync", loginId, confirmedByUser: true };
}

// ---- DOM ladder clicks (SPEC §13.1, §9.1; Tradovate) ------------------------------------------

export type DomColumn = "bid" | "ask";

export type DomClick = {
  column: DomColumn;
  priceTicks: number;
  /** Shift+click or right-click: a stop instead of a limit (Tradovate). */
  stop: boolean;
  bestBid: number | null;
  bestAsk: number | null;
};

export type DomOrder =
  { ok: true; side: TradeSide; type: "limit" | "stop"; priceTicks: number } | { ok: false; reason: string };

/**
 * What a click in a DOM order column means. Bid column = buy, ask column = sell. Plain click =
 * limit at that price; shift/right-click = stop market at that price.
 *
 * Refused (never "fixed up" into another order):
 * - a buy stop not **above the best ask**, a sell stop not **below the best bid** (§9.1
 *   `STOP_WOULD_TRIGGER`);
 * - a buy limit above the best ask or a sell limit below the best bid: it would trade through
 *   the market at once — use a market order (conservative; the gateway price collar is not
 *   wired yet);
 * - anything without a best bid and ask.
 */
export function domClickOrder(c: DomClick): DomOrder {
  if (c.bestBid === null || c.bestAsk === null) return { ok: false, reason: "No best bid/ask yet" };
  const side: TradeSide = c.column === "bid" ? "buy" : "sell";
  if (c.stop) {
    if (side === "buy" && c.priceTicks <= c.bestAsk) {
      return { ok: false, reason: "Stop would trigger immediately. Use a market or limit order." };
    }
    if (side === "sell" && c.priceTicks >= c.bestBid) {
      return { ok: false, reason: "Stop would trigger immediately. Use a market or limit order." };
    }
    return { ok: true, side, type: "stop", priceTicks: c.priceTicks };
  }
  if (side === "buy" && c.priceTicks > c.bestAsk) {
    return { ok: false, reason: "A buy limit above the best ask would fill at once. Use a market order." };
  }
  if (side === "sell" && c.priceTicks < c.bestBid) {
    return { ok: false, reason: "A sell limit below the best bid would fill at once. Use a market order." };
  }
  return { ok: true, side, type: "limit", priceTicks: c.priceTicks };
}

/** Join Bid = buy limit at the best bid; Join Ask = sell limit at the best ask (SPEC §9.2). */
export function joinPrice(side: TradeSide, bestBid: number | null, bestAsk: number | null): number | null {
  return side === "buy" ? bestBid : bestAsk;
}

/** Price an own working order sits at on the ladder: a stop (market or limit) at its trigger, else its limit. */
export function ladderPrice(o: Pick<OrderEvent, "limitTicks" | "stopTicks" | "type">): number | null {
  const type = num(o.type);
  if (type === PB_ORDER_TYPE.STOP_MARKET || type === PB_ORDER_TYPE.STOP_LIMIT) {
    return o.stopTicks !== 0n ? Number(o.stopTicks) : null;
  }
  if (o.limitTicks !== 0n) return Number(o.limitTicks);
  if (o.stopTicks !== 0n) return Number(o.stopTicks);
  return null;
}

export type OwnOrders = { buy: OrderEvent[]; sell: OrderEvent[] };

/** Working orders of (account, contract) by ladder price, for My Bid / My Ask. */
export function ownOrdersByPrice(
  orders: Iterable<OrderEvent>,
  accountId: string | null,
  contract: string | null,
): Map<number, OwnOrders> {
  const out = new Map<number, OwnOrders>();
  if (accountId === null || contract === null) return out;
  for (const o of orders) {
    if (o.accountId !== accountId || o.symbol !== contract || !isWorkingOrder(o)) continue;
    const px = ladderPrice(o);
    if (px === null) continue;
    let slot = out.get(px);
    if (!slot) {
      slot = { buy: [], sell: [] };
      out.set(px, slot);
    }
    (num(o.side) === PB_SIDE.BUY ? slot.buy : slot.sell).push(o);
  }
  return out;
}

/** Remaining (unfilled) quantity of orders. */
export function openQty(orders: readonly Pick<OrderEvent, "qty" | "filled">[]): number {
  return orders.reduce((n, o) => n + Math.max(0, o.qty - o.filled), 0);
}

/** Orders that can be modified: working, not already changing, not UNKNOWN. */
const MODIFIABLE: ReadonlySet<number> = new Set<number>([
  ORDER_STATE.WORKING,
  ORDER_STATE.PARTIALLY_FILLED,
  ORDER_STATE.TRIGGERED,
]);

/** Orders that can be cancelled: the modifiable ones, and one whose modify is unanswered (the
 * gateway sends the cancel, which supersedes the modify — audit H4). */
const CANCELLABLE: ReadonlySet<number> = new Set<number>([...MODIFIABLE, ORDER_STATE.PENDING_MODIFY]);

export function isModifiable(o: Pick<OrderEvent, "state">): boolean {
  return MODIFIABLE.has(o.state);
}

export function isCancellable(o: Pick<OrderEvent, "state">): boolean {
  return CANCELLABLE.has(o.state);
}

/** The former single predicate (cancellable, which includes PENDING_MODIFY), kept so callers on
 * other branches still build; new code picks `isCancellable` or `isModifiable`. */
export const isActionable = isCancellable;

/** A proto enum value as a plain number (compared with the `ORDER_STATE` mirror). */
const stateNum = (o: Pick<OrderEvent, "state">): number => o.state;

/**
 * Why none of `list` can be cancelled right now (each is waiting on Rithmic), or null when one of
 * them can (or the list is empty). An order whose modify is unanswered can be cancelled (audit H4).
 */
export function notActionableReason(list: readonly Pick<OrderEvent, "state">[]): string | null {
  if (list.length === 0 || list.some(isCancellable)) return null;
  const has = (s: number): boolean => list.some((o) => stateNum(o) === s);
  if (has(ORDER_STATE.PENDING_CANCEL)) return "A cancel is already on its way";
  if (has(ORDER_STATE.UNKNOWN)) {
    return "The order is being reconciled with Rithmic (UNKNOWN); it can be cancelled once its state is known";
  }
  if (has(ORDER_STATE.PENDING_SUBMIT)) {
    return "Waiting for Rithmic to acknowledge the order; cancel it once it shows as working";
  }
  return "The order is changing; try again in a moment";
}

/**
 * Short label for a live order that cannot be modified now ("Cancelling", "Modifying",
 * "Reconciling", "Pending"); null when it can. A "Modifying" order can still be cancelled.
 */
export function pendingLabel(o: Pick<OrderEvent, "state">): string | null {
  if (isModifiable(o)) return null;
  const s = stateNum(o);
  if (s === ORDER_STATE.PENDING_CANCEL) return "Cancelling";
  if (s === ORDER_STATE.PENDING_MODIFY) return "Modifying";
  if (s === ORDER_STATE.UNKNOWN) return "Reconciling";
  return "Pending";
}

/** Which sides of (account, contract) have an order in UNKNOWN (reconciling, SPEC §6.9). */
export function unknownSides(
  orders: Iterable<OrderEvent>,
  accountId: string | null,
  contract: string | null,
): { buy: boolean; sell: boolean } {
  const r = { buy: false, sell: false };
  if (accountId === null || contract === null) return r;
  for (const o of orders) {
    if (o.accountId !== accountId || o.symbol !== contract || num(o.state) !== ORDER_STATE.UNKNOWN) continue;
    if (num(o.side) === PB_SIDE.BUY) r.buy = true;
    else r.sell = true;
  }
  return r;
}

/**
 * The `ModifyOrder` change that moves an own order to `toTicks` (DOM drag): a limit moves its
 * limit, a stop its trigger; a stop-limit moves both by the same number of ticks. null when the
 * order has no price or would not move.
 */
export function dragChange(
  o: Pick<OrderEvent, "type" | "limitTicks" | "stopTicks">,
  toTicks: number,
): { limitTicks?: number; stopTicks?: number } | null {
  const from = ladderPrice(o);
  if (from === null || from === toTicks || toTicks <= 0) return null;
  const type = num(o.type);
  if (type === PB_ORDER_TYPE.STOP_LIMIT) {
    const delta = toTicks - from;
    const limit = Number(o.limitTicks) + delta;
    const stop = Number(o.stopTicks) + delta;
    if (limit <= 0 || stop <= 0) return null;
    return { limitTicks: limit, stopTicks: stop };
  }
  if (type === PB_ORDER_TYPE.STOP_MARKET) return { stopTicks: toTicks };
  return { limitTicks: toTicks };
}

const TYPE_WORDS: Readonly<Record<EntryType, string>> = {
  market: "Market",
  limit: "Limit",
  stop: "Stop",
  stopLimit: "Stop Limit",
};

/** The order in words (SPEC §9.2 "Submit button repeats the order in words"). */
export function orderWords(i: OrderIntent, price: (ticks: number) => string): string {
  if (i.protectPosition === true) {
    // "Take profit: Sell 3 NQZ6 limit @ 21,460.00 · whole position, OCO with its SL".
    const tp = i.type === "limit";
    const px = tp ? i.limitTicks : i.stopTicks;
    return `${tp ? "Take profit" : "Stop loss"}: ${i.side === "buy" ? "Buy" : "Sell"} ${String(i.qty)} ${i.contract} ${tp ? "limit" : "stop"} @ ${px === undefined ? "?" : price(px)} · whole position, OCO with its ${tp ? "SL" : "TP"}`;
  }
  const parts = [`${i.side === "buy" ? "Buy" : "Sell"} ${String(i.qty)} ${i.contract} ${TYPE_WORDS[i.type]}`];
  if ((i.type === "stop" || i.type === "stopLimit") && i.stopTicks !== undefined) {
    parts.push(`stop ${price(i.stopTicks)}`);
  }
  if ((i.type === "limit" || i.type === "stopLimit") && i.limitTicks !== undefined) {
    parts.push(`${i.type === "limit" ? "@" : "limit"} ${price(i.limitTicks)}`);
  }
  const words = parts.join(" ");
  return i.bracket ? `${words} · ${bracketWords(i.bracket)}` : words;
}

/** Short role tag of a bracket child for the ladder and tables: "TP1", "SL2"; null otherwise. */
export function bracketTag(o: Pick<OrderEvent, "bracketRole" | "bracketLeg">): string | null {
  const role = num(o.bracketRole);
  if (role === PB_BRACKET_ROLE.TARGET) return `TP${String(o.bracketLeg || 1)}`;
  if (role === PB_BRACKET_ROLE.STOP) return `SL${String(o.bracketLeg || 1)}`;
  return null;
}
